import React from 'react';
import ConfirmDialog from '../ConfirmDialog';
import { hireMoneyWarningTitle, hireMoneyWarningMessage, closedHireStatusLabel } from '../../utils/hireMoneyWarning';

/**
 * The confirm-before-money-changes gate shared by HireActionModal's Extend
 * flow and GanttView's drag-to-extend/shorten/change-start flow — see
 * `utils/hireMoneyWarning.js` for the wording rules and why they always
 * fire. Built on the shared `ConfirmDialog` (focus trap, Escape, overlay
 * close, focus return) rather than hand-rolled, per
 * `components/ConfirmDialog.js`'s own docblock: "A fifth hand-rolled modal
 * in this repo would get some subset of them subtly wrong."
 *
 * `variant` is 'danger' when the order is already closed (approved,
 * ordered, delivered, or the item is off-hired) — the visual half of
 * "sharper wording" for an already-closed record.
 *
 * `altLabel`/`onAlt`/`altVariant` (2026-09-02, hire extend/shorten/
 * change-start dialog spec) — the optional second COMMITTING button
 * ("Extend & view order" etc.), passed straight through to `ConfirmDialog`.
 * All three default to `undefined`, so a caller that omits them renders
 * exactly two buttons, byte-identical to before this addition.
 */
export default function HireMoneyWarningDialog({
  open,
  action,
  item,
  busy,
  onConfirm,
  onCancel,
  confirmLabel = 'Update price and continue',
  altLabel,
  onAlt,
  altVariant,
  children,
}) {
  const isClosed = !!closedHireStatusLabel(item);
  return (
    <ConfirmDialog
      open={open}
      title={hireMoneyWarningTitle(item)}
      message={hireMoneyWarningMessage(action, item)}
      variant={isClosed ? 'danger' : 'default'}
      // The confirm is BLUE in every case (owner ruling 2026-09-25): a closed
      // hire keeps its red TITLE as the sharper wording, not a red button.
      confirmVariant="primary"
      confirmLabel={confirmLabel}
      cancelLabel="Cancel"
      altLabel={altLabel}
      onAlt={onAlt}
      altVariant={altVariant}
      busy={busy}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {children}
    </ConfirmDialog>
  );
}
